import React, { Component, createContext, useContext } from 'react'
import { useAuth } from 'contexts/auth'
import { useIntegrations } from 'contexts/integrations'
import { get, post, patch, remove } from 'api'
import { pick, omit } from 'utilities/object'
import { createLogger } from 'utilities/rapid7-insight-ops'
import { local } from 'utilities/storage'
import queryString from 'query-string'

const integrationLogger = createLogger('log', 'AUDIT:')

const IntegrationContext = createContext()
IntegrationContext.displayName = 'Integration'

// not_configured
// configured
// connected
// active
// authorization_failure

class IntegrationProvider extends Component {
    constructor(props) {
        super(props)

        this.setType(props.type)

        this.state = {
            integration: null,
            oauth: null,

            fetchIntegration: this.fetch,
            configure: this.configure,
            connect: this.connect,
            update: this.update,
            compare: this.compare,
            fetchAbsenceTypes: this.fetchAbsenceTypes,
            sync: this.sync,
            exportData: this.export,
            disconnect: this.disconnect,
            remove: this.remove
        }
    }

    componentDidMount() {
        this.fetch()
    }

    componentDidUpdate({ type }, { integration }) {
        if(type !== this.props.type) {
            this.replace(this.props.type)
        }

        if(!!this.state.integration?.status && integration?.status !== this.state.integration?.status) {
            this.props.integrations.updateIntegration(
                this.props.type,
                pick(this.state.integration, 'status')
            )
        }
    }

    componentWillUnmount() {
        global.removeEventListener('message', this.handleOauthResponse)
    }

    setType = type => this.type = type

    fetch = async () => {
        if(!this.type) {
            return
        }

        const { ok, response: integration } = await get({ path: `/integrations/${this.type}` })

        !!ok && this.setState({ integration })

        return { ok, response: integration }
    }

    configure = async (body, options = {}) => {
        const { connect = true } = options
        const { integration } = this.state

        let configureResult = { ok: true, response: integration }
        let connectResult = { ok: true }

        const unconfigured = integration.status === 'not_configured'

        const configureMethod = unconfigured ?
            post :
            patch

        const path = unconfigured ?
            '/integrations' :
            `/integrations/${integration.type}`

        if(unconfigured) {
            body.type = this.type

            const withDefaults = source => (accumulator, { type, name, required }) => {
                const defaultValue = {
                    boolean: false,
                    string: null,
                    url: null
                }[type]

                const value = (name in source) ?
                    source[name] :
                    required ?
                        defaultValue :
                        undefined

                if(value === undefined) {
                    return accumulator
                }

                return {
                    ...accumulator,
                    [name]: value
                }
            }

            const authorizationParams = integration.authorizationOptions?.find(({ type }) => type === body.authorization.type)?.params
            if(!!authorizationParams?.length) {
                body.authorization.params = authorizationParams.reduce(withDefaults(body.authorization?.params ?? {}), {})
            }

            if(!!integration.configParams?.length) {
                body.configParams = integration.configParams.reduce(withDefaults(body.configParams ?? {}), {})
            }
        } else {
            if(!!body.authorization?.params) {
                body.authorization.params = Object.entries(body.authorization.params)
                    .map(([name, value]) => [name, value, integration.authorization?.params?.find(param => param.name === name)])
                    .filter(([, value, field]) => {
                        if(value === null) {
                            return true
                        }

                        return value !== field?.value
                    })
                    .reduce((accumulator, [name, value, field]) => {
                        const boolean = field?.type === 'boolean'
                        if(!boolean && (!value && value !== 0 && value !== null)) {
                            return accumulator
                        }

                        return {
                            ...accumulator,
                            [name]: boolean ?
                                !!value :
                                value
                        }
                    }, {})
            }

            if(body.configuration) {
                body.configuration = Object.entries(body.configuration)
                    .map(([name, value]) => [name, value, integration.configParams?.find(param => param.name === name)])
                    .filter(([, value, field]) => {
                        if(value === null) {
                            return true
                        }

                        return value !== field?.value
                    })
                    .reduce((accumulator, [name, value, field]) => {
                        const boolean = field?.type === 'boolean'
                        if(!boolean && (!value && value !== 0 && value !== null)) {
                            return accumulator
                        }

                        return {
                            ...accumulator,
                            [name]: boolean ?
                                !!value :
                                value
                        }
                    }, {})
            }

            if(!unconfigured && (body.authorization?.type === integration.authorization?.type) && !body.authorization.params) {
                delete body.authorization
            }
        }

        configureResult = await configureMethod({ path, body })

        if(connect) {
            if(integration.status === 'configured') {
                connectResult = await this.connect(integration)
            } else if(configureResult.ok && configureResult.response.status === 'configured') {
                connectResult = await this.connect(configureResult.response)
            }
        }

        if(connect && connectResult.ok && connectResult.response) {
            this.setState({ integration: connectResult.response })
        } else if(configureResult.ok && configureResult.response) {
            this.setState({ integration: configureResult.response })
        }

        return {
            ok: ((connect && connectResult.ok) || !connect) && configureResult.ok,
            response: connectResult?.response ?? configureResult?.response
        }
    }

    connect = async (integration = this.state.integration) => {
        const redirectUri = this.buildRedirectUri()

        let OAuthResult = null

        if(integration.authorization?.type === 'oauth2') {
            const popupUrl = queryString.stringifyUrl({
                url: integration.authorization.authorizationUrl,
                query: {
                    redirect_uri: redirectUri,
                    state: this.getRedirectState()
                }
            })

            try {
                OAuthResult = await new Promise((resolve, reject) => {
                    const settle = handle => {
                        handle()

                        this.setState(({ oauth }) => ({
                            oauth: {
                                ...omit(oauth, 'resolve', 'reject'),
                                settled: true
                            }
                        }))
                    }

                    this.setState(({ oauth }) => ({
                        oauth: {
                            ...oauth,
                            resolve: (...args) => settle(() => resolve(...args)),
                            reject: (...args) => settle(() => reject(...args)),
                            settled: false
                        }
                    }), () => this.openOAuthPopup(
                        popupUrl,
                        integration.name
                    ))
                })
            } catch(error) {
                OAuthResult = {
                    error: true,
                    details: error
                }
            }

            if(OAuthResult?.error || !OAuthResult?.code) {
                integrationLogger({
                    source: 'web',
                    event: 'integration-oauth-error',
                    result: OAuthResult,
                    orgId: local.get('ids')?.organization,
                    userId: local.get('ids')?.user
                })

                return {
                    ok: false,
                    response: {
                        error: 'oauth'
                    }
                }
            }
        }

        const { ok, response } = await post({
            path: `/integrations/${this.type}/connect`,
            body: {
                type: integration.authorization?.type,
                ...((integration.authorization?.type === 'oauth2') ? {
                    grant: {
                        type: 'authorization_code',
                        code: OAuthResult?.code,
                        redirectUri
                    }
                } : null)
            }
        })

        !!ok && this.setState({ integration: response })

        return { ok, response }
    }

    update = async body => {
        const { response: integration, ok } = await patch({
            path: `/integrations/${this.type}`,
            body
        })

        !!ok && this.setState({ integration })

        return { ok, response: integration }
    }

    disconnect = async () => {
        const { ok, response: integration } = await remove({
            path: `/integrations/${this.type}/connect`
        })

        ok && this.setState({ integration })

        return { ok, response: integration }
    }

    remove = async () => {
        const { ok } = await remove({
            path: `/integrations/${this.type}`,
            returnsData: false
        })

        let response = null

        if(ok) {
            const fetchResult = await this.fetch()

            if(fetchResult.ok) {
                response = fetchResult.response
            }
        }

        return { ok, response }
    }

    export = async body => {
        const { response, ok } = await post({
            path: `/integrations/${this.type}/export`,
            body,
            returnsData: false
        })

        return { response, ok }
    }

    compare = async () => {
        const { response, ok } = await get({
            path: `/integrations/${this.type}/compare`
        })

        return { response, ok }
    }

    fetchAbsenceTypes = async () => {
        const { response, ok } = await get({
            path: `/integrations/${this.type}/absences/types`
        })

        return { response, ok }
    }

    sync = async (body = {}) => {
        const { status: initialStatus } = this.state.integration
        this.setStatus('active')

        const { ok } = await post({
            path: `/integrations/${this.type}/sync`,
            body,
            returnsData: false
        })

        !ok && this.setStatus(initialStatus)

        return { ok }
    }

    setStatus = status => void this.setState(({ integration }) => ({
        integration: {
            ...integration,
            status
        }
    }))

    handleOauthResponse = ({ data }) => {
        const { oauth } = this.state
        const associated = data.state === this.getRedirectState()

        if(!associated || !!oauth?.settled) {
            return
        }

        if(!!data?.code) {
            oauth.resolve({
                code: data.code,
                redirectUri: this.buildRedirectUri()
            })
        } else if(data?.action === 'closed' || !!data?.error) {
            oauth.reject()
        }
    }

    openOAuthPopup = (url, name) => {
        global.removeEventListener('message', this.handleOauthResponse)
        global.addEventListener('message', this.handleOauthResponse)

        const { oauth } = this.state

        let {
            popup,
            previousUrl
        } = oauth ?? {}

        if(!popup || popup?.closed || previousUrl !== url) {
            popup = global.open(url, name, OAuthPopupSettings)

            this.setState(({ oauth }) => ({
                oauth: {
                    ...oauth,
                    popup,
                    previousUrl: url
                }
            }))
        }

        const interval = global.setInterval(() => {
            if(popup?.closed) {
                this.handleOauthResponse({
                    data: {
                        error: true,
                        state: this.getRedirectState()
                    }
                })

                global.clearInterval(interval)
            }
        }, 500)

        popup?.focus()
    }

    buildRedirectUri() {
        const { protocol, hostname, host } = global.location
        const localhost = hostname === 'localhost'
        const heroku = hostname.endsWith('.herokuapp.com')

        const redirectHost = localhost ?
            host :
            heroku ?
                hostname :
                `auth.${host.split('.').slice(1).join('.')}`

        return `${protocol}//${redirectHost}/oauth`
    }

    getRedirectState = () => JSON.stringify({
        key: this.props.auth.key,
        subject: this.props.type
    })

    replace = type => {
        this.setType(type)

        this.setState({
            integration: null,
            oauth: null
        }, this.fetch)
    }

    render() {
        const { children = null } = this.props

        return (
            <IntegrationContext.Provider value={this.state}>
                {(typeof children === 'function') && children(this.state)}
                {(typeof children !== 'function') && children}
            </IntegrationContext.Provider>
        )
    }
}

const OAuthPopupSettings = 'toolbar=no, menubar=no, width=600, height=700, top=100, left=100'

export const useIntegration = () => useContext(IntegrationContext)

export default props => {
    const auth = useAuth()
    const integrations = useIntegrations()

    return (
        <IntegrationProvider
            {...props}
            auth={auth}
            integrations={integrations} />
    )
}